import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useInView } from 'react-intersection-observer';
import AnimatedUnderline from './common/AnimatedUnderline';

const SectionWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '160px 0',
  background: '#FFFFFF',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: '600px',
    background: 'linear-gradient(180deg, rgba(0,75,135,0.03) 0%, rgba(255,255,255,0) 100%)',
    transform: 'skewY(-6deg)',
    transformOrigin: 'top left',
  }
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '4rem',
  alignItems: 'start',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
    gap: '3rem',
  }
}));

const TextContent = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(2rem, 5vw, 2.75rem)',
  fontWeight: 300,
  color: '#1A365D',
  marginBottom: '0.5rem',
  fontFamily: 'Poppins, sans-serif',
  letterSpacing: '-0.02em',
  textAlign: 'left',
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
  lineHeight: 1.8,
  color: '#475569',
  marginBottom: '2rem',
  '& strong': {
    color: '#004B87',
    fontWeight: 500,
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: '#475569',
  marginTop: '1rem',
  marginBottom: '3rem',
  maxWidth: '800px',
  textAlign: 'left',
  lineHeight: 1.6,
}));

const AccreditationsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '1.5rem',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '1rem',
  }
}));

const AccreditationCard = styled(Box)(({ theme, inView }) => ({
  position: 'relative',
  padding: '2rem',
  background: 'rgba(255, 255, 255, 1)',
  borderRadius: '16px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(0, 75, 135, 0.15)',
  boxShadow: '0 4px 20px rgba(0, 75, 135, 0.08)',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.95)',
  '&:hover': {
    transform: 'translateY(-8px)',
    border: '1px solid rgba(0, 75, 135, 0.25)',
    boxShadow: '0 20px 40px rgba(0, 75, 135, 0.15)',
    '& .accreditation-title': {
      background: 'linear-gradient(45deg, #73c043, #8fd458)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    '& .accreditation-bg': {
      opacity: 1,
      transform: 'scale(1)',
    }
  }
}));

const CardBackground = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at top right, rgba(0,75,135,0.03) 0%, rgba(255,255,255,0) 70%)',
  borderRadius: '16px',
  opacity: 0,
  transform: 'scale(0.8)',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const AccreditationTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.1rem, 1.5vw, 1.25rem)',
  fontWeight: 700,
  background: 'linear-gradient(45deg, #004B87, #0066b8)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  position: 'relative',
  transition: 'all 0.3s ease',
  textAlign: 'left',
  zIndex: 1,
  paddingLeft: '15px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '4px',
    height: '80%',
    background: '#73c043',
    borderRadius: '2px',
  }
}));

const WrapperCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  borderRadius: '20px',
  padding: '2rem',
  boxShadow: '0 8px 32px rgba(0, 75, 135, 0.05)',
  border: '1px solid rgba(0, 75, 135, 0.08)',
}));

const WrapperTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 500,
  color: '#004B87',
  marginBottom: '2rem',
  textAlign: 'left',
}));

const accreditations = [
  
  { title: 'Accréditation COFRAC Etalonnage' },
  { title: 'Accréditation COFRAC Essais' },
  { title: 'Accréditation TUNAC' },
  { title: 'Accréditation SEMAC' },
];

const AccreditationSection = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <SectionWrapper>
      <Container 
        maxWidth="xl"
        sx={{
          px: { xs: 2, sm: 4, md: 8, lg: 12 },
        }}
      >
        <ContentWrapper>
          <TextContent>
            <Title variant="h2">
              Nos Accréditations
            </Title>
            <AnimatedUnderline align="left" sx={{ marginTop: '-0.3rem', marginBottom: '0.5rem' }} />
            <Subtitle>
              Des certifications internationales qui témoignent de notre engagement envers l'excellence et la qualité
            </Subtitle>
            <Description>
            Depuis 2005, le laboratoire L2MI et, depuis 2007, le laboratoire AIR METROLOGIE ont entrepris une démarche qualité selon la norme <strong>ISO/IEC 17 025 version 2017</strong> visant à assurer la conformité des prestations fournies, afin de répondre aux attentes de leurs clients.
            Notre objectif est d'offrir à nos clients 
              le plus haut niveau de service en métrologie, contribuant ainsi à l'amélioration continue de leurs processus de mesure. 
              Cette démarche renforce la fiabilité et la rigueur de notre fonctionnement.
            </Description>
          </TextContent>

          <WrapperCard ref={ref}>
            <WrapperTitle>ISO/IEC 17 025 version 2017</WrapperTitle>
            <AccreditationsGrid>
              {accreditations.map((accreditation, index) => (
                <AccreditationCard 
                  key={index}
                  inView={inView}
                  sx={{
                    transitionDelay: `${index * 100}ms`,
                  }}
                >
                  <CardBackground className="accreditation-bg" />
                  <AccreditationTitle className="accreditation-title">
                    {accreditation.title}
                  </AccreditationTitle>
                </AccreditationCard>
              ))}
            </AccreditationsGrid>
          </WrapperCard>
        </ContentWrapper>
      </Container>
    </SectionWrapper>
  );
};

export default AccreditationSection;
